import { ValidationService } from "@/domain/records/services/ValidationService";
import { SetupConfig } from "@/domain/records/value-objects/SetupConfig";
import { GenerationRequest } from "@/domain/records/value-objects/GenerationRequest";
import type { ValidationResult } from "@/domain/records/types";

// Type definitions for field validation
type FieldValue = string | number | string[] | undefined;

interface ValidationContext {
  reportTypes?: string[];
  participantCount?: number;
  respondentNames?: string[];
}

/**
 * Use Case for validating setup configurations
 * Clean Architecture - Application Layer
 */
export class ValidateSetupUseCase {
  /**
   * Validate complete setup configuration
   */
  validateSetupConfig(config: SetupConfig): ValidationResult {
    return ValidationService.validateCompleteSetupForm(config);
  }

  /**
   * Validate individual field
   */
  validateField(
    fieldName: string,
    value: FieldValue,
    context?: ValidationContext
  ): ValidationResult {
    switch (fieldName) {
      case "recordName":
        return ValidationService.validateRecordName(value as string);

      case "goals":
      case "researchContext":
        return ValidationService.validateResearchContext(value as string);

      case "customPrompt":
        const isCustomSelected =
          context?.reportTypes?.includes("Custom") ?? false;
        return ValidationService.validateCustomPrompt(
          value as string,
          isCustomSelected
        );

      case "participantCount":
        return ValidationService.validateParticipantCount(value as number);

      case "respondentNames":
        return ValidationService.validateRespondentNames(value as string[]);

      case "version":
        return ValidationService.validateVersion(value as number);

      default:
        return { isValid: true, errors: [] };
    }
  }

  /**
   * Validate generation request
   */
  validateGenerationRequest(request: GenerationRequest): ValidationResult {
    return ValidationService.validateGenerationRequest(request);
  }

  /**
   * Validate file upload
   */
  validateFileUpload(file: {
    size: number;
    type: string;
    name: string;
  }): ValidationResult {
    return ValidationService.validateFileUpload(file);
  }

  /**
   * Get validation error messages for UI
   */
  getValidationMessages(result: ValidationResult): {
    hasErrors: boolean;
    messages: string[];
    summary: string;
  } {
    return {
      hasErrors: !result.isValid,
      messages: result.errors,
      summary:
        result.errors.length > 0
          ? `${result.errors.length} validation error${
              result.errors.length > 1 ? "s" : ""
            } found`
          : "All validations passed",
    };
  }

  /**
   * Check if setup can be submitted
   */
  canSubmitSetup(config: SetupConfig): {
    canSubmit: boolean;
    blockingErrors: string[];
    warnings: string[];
  } {
    const validation = this.validateSetupConfig(config);
    const blockingErrors: string[] = [];
    const warnings: string[] = [];

    // Categorize errors
    validation.errors.forEach((error) => {
      if (error.includes("required") || error.includes("must be")) {
        blockingErrors.push(error);
      } else {
        warnings.push(error);
      }
    });

    return {
      canSubmit: validation.isValid,
      blockingErrors,
      warnings,
    };
  }

  /**
   * Validate setup changes for regeneration
   */
  validateSetupChanges(
    originalConfig: SetupConfig,
    newConfig: SetupConfig
  ): {
    hasChanges: boolean;
    requiresRegeneration: boolean;
    changeSummary: string[];
  } {
    const changes: string[] = [];
    let requiresRegeneration = false;

    // Check report types
    const originalTypes = new Set(originalConfig.reportTypes);
    const newTypes = new Set(newConfig.reportTypes);

    if (
      originalTypes.size !== newTypes.size ||
      !Array.from(originalTypes).every((type) => newTypes.has(type))
    ) {
      changes.push("Report types changed");
      requiresRegeneration = true;
    }

    // Check custom prompt
    if (
      newConfig.reportTypes.includes("Custom") &&
      originalConfig.customPrompt !== newConfig.customPrompt
    ) {
      changes.push("Custom prompt changed");
      requiresRegeneration = true;
    }

    // Check research context (significant changes)
    if (originalConfig.goals !== newConfig.goals) {
      const originalLength = originalConfig.goals?.length ?? 0;
      const newLength = newConfig.goals?.length ?? 0;

      if (Math.abs(originalLength - newLength) > 50) {
        changes.push("Research context significantly changed");
        requiresRegeneration = true;
      } else {
        changes.push("Research context modified");
      }
    }

    // Check other fields (non-regeneration requiring)
    if (originalConfig.participantCount !== newConfig.participantCount) {
      changes.push("Participant count changed");
    }

    if (
      JSON.stringify(originalConfig.respondentNames) !==
      JSON.stringify(newConfig.respondentNames)
    ) {
      changes.push("Respondent names changed");
    }

    if (originalConfig.language !== newConfig.language) {
      changes.push("Report language changed");
    }

    return {
      hasChanges: changes.length > 0,
      requiresRegeneration,
      changeSummary: changes,
    };
  }
}
